import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import Navbar from '@/components/Navbar';
import {
  Trophy, Target, Clock, TrendingUp, Calendar, ChevronRight, Download, FileText, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCENARIO_LABELS = {
  'giving-feedback': 'Feedback',
  'difficult-conversations': 'Difficult Talks',
  'situational-leadership': 'Leadership',
  'decision-making': 'Decisions',
  'critical-thinking': 'Critical Think.',
};

const DIFF_BADGE = {
  beginner: 'bg-success/10 text-success',
  intermediate: 'bg-warning/10 text-warning',
  advanced: 'bg-accent/10 text-accent',
};

export default function ProgressPage() {
  const navigate = useNavigate();
  const { axiosAuth, token } = useAuth();
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const api = axiosAuth();
    Promise.all([api.get('/progress'), api.get('/progress/history')])
      .then(([progRes, histRes]) => { setProgress(progRes.data); setHistory(histRes.data.history); })
      .catch(() => toast.error('Failed to load progress'))
      .finally(() => setLoading(false));
  }, [axiosAuth]);

  const downloadReport = (simId, type) => {
    fetch(`${API}/simulations/${simId}/report?report_type=${type}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `report_${type}.pdf`; a.click(); })
      .catch(() => toast.error('Report only available for completed simulations'));
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="heritage-card p-8 text-center"><div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="font-body text-sm">Loading progress...</p></div>
    </div>
  );

  const barData = Object.entries(progress?.scenario_averages || {}).map(([id, score]) => ({
    name: SCENARIO_LABELS[id] || id, score, fill: 'var(--brand)'
  }));

  const completedHistory = history.filter(h => h.status === 'completed' && h.score);
  const latestScores = completedHistory.length > 0 ? completedHistory[0].score : null;
  const radarData = latestScores ? [
    { subject: 'Communication', A: latestScores.communication_clarity },
    { subject: 'Empathy', A: latestScores.empathy },
    { subject: 'Problem Solving', A: latestScores.problem_solving },
    { subject: 'Professionalism', A: latestScores.professionalism },
    { subject: 'Effectiveness', A: latestScores.outcome_effectiveness },
  ] : [];

  const stats = [
    { label: 'Total', value: progress?.total_simulations || 0, icon: Target, color: 'text-brand' },
    { label: 'Completed', value: progress?.completed || 0, icon: Trophy, color: 'text-success' },
    { label: 'In Progress', value: progress?.in_progress || 0, icon: Clock, color: 'text-warning' },
    { label: 'Avg Score', value: progress?.overall_average_score ? `${progress.overall_average_score}/10` : '--', icon: TrendingUp, color: 'text-brand' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="progress-page">
      <Navbar showBack backTo="/" backLabel="Progress" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Your Progress</h1>
            <p className="font-body text-sm text-muted-foreground mt-1">Track your simulation history and skill development</p>
          </div>
          <button data-testid="export-progress-csv" onClick={() => {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/csv/my-progress`, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'my-progress.csv'; a.click(); })
              .catch(() => toast.error('Export failed'));
          }} className="h-8 px-3 bg-transparent border border-border text-foreground hover:bg-muted gap-1.5 text-xs font-medium rounded-lg flex items-center">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="heritage-card p-4" data-testid={`progress-stat-${s.label.toLowerCase().replace(' ', '-')}`}>
              <div className="flex items-center gap-2 mb-1">
                <s.icon className={`w-4 h-4 ${s.color}`} />
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{s.label}</span>
              </div>
              <p className="font-heading text-xl sm:text-2xl font-bold">{s.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="charts" className="space-y-5">
          <TabsList className="border border-border bg-surface p-1 shadow-soft" data-testid="progress-tabs">
            <TabsTrigger value="charts" className="font-heading font-semibold data-[state=active]:bg-brand data-[state=active]:text-[#333333] rounded-md px-4 text-sm">Charts</TabsTrigger>
            <TabsTrigger value="history" className="font-heading font-semibold data-[state=active]:bg-brand data-[state=active]:text-[#333333] rounded-md px-4 text-sm">History</TabsTrigger>
          </TabsList>

          <TabsContent value="charts">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="heritage-card p-4 sm:p-6" data-testid="score-bar-chart">
                <h3 className="font-heading text-sm sm:text-base font-bold mb-4">Average by Scenario</h3>
                {barData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={barData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: 'Inter' }} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                      <ReTooltip contentStyle={{ border: '1px solid var(--border)', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontFamily: 'Inter', backgroundColor: 'var(--surface)' }} />
                      <Bar dataKey="score" radius={[4, 4, 0, 0]} strokeWidth={0} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center"><p className="font-body text-sm text-muted-foreground">Complete simulations to see scores</p></div>
                )}
              </div>
              <div className="heritage-card p-4 sm:p-6" data-testid="skill-radar-chart">
                <h3 className="font-heading text-sm sm:text-base font-bold mb-4">Skills Breakdown (Latest)</h3>
                {radarData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="var(--border)" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fontFamily: 'Inter' }} />
                      <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 9 }} />
                      <Radar name="Score" dataKey="A" stroke="var(--brand)" fill="var(--brand)" fillOpacity={0.15} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center"><p className="font-body text-sm text-muted-foreground">Complete a simulation to see breakdown</p></div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history">
            <div className="space-y-2" data-testid="history-list">
              {history.length === 0 ? (
                <div className="heritage-card p-8 text-center">
                  <Trophy className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="font-heading text-base font-bold mb-1">No simulations yet</p>
                  <p className="font-body text-sm text-muted-foreground mb-4">Start your first simulation to build your history</p>
                  <Button onClick={() => navigate('/')} className="bg-brand text-[#333333] font-medium rounded-lg h-9 px-4 text-sm hover:opacity-90">Start Practicing</Button>
                </div>
              ) : (
                history.map((sim, i) => (
                  <div key={sim.id} className="heritage-card-hover p-3 sm:p-4 flex items-center gap-3 sm:gap-4" data-testid={`history-item-${i}`}>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/simulation/${sim.id}`)}>
                      <p className="font-heading text-sm font-bold truncate">{sim.scenario_title}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <Badge className={`${DIFF_BADGE[sim.difficulty] || 'bg-muted'} border-none font-body text-[10px] px-1.5 py-0`}>{sim.difficulty}</Badge>
                        {sim.variation_title && sim.variation_title !== 'Default' && (
                          <Badge className="bg-muted text-muted-foreground border-none font-body text-[10px] px-1.5 py-0">{sim.variation_title}</Badge>
                        )}
                        <span className="font-body text-[10px] text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {new Date(sim.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {sim.status === 'completed' && sim.score ? (
                        <>
                          <p className="font-heading text-base sm:text-lg font-bold">{sim.score.overall_score}/10</p>
                          <button data-testid={`view-report-${i}`} onClick={() => navigate(`/report/${sim.id}`)}
                            className="w-7 h-7 border border-border rounded-md flex items-center justify-center hover:bg-brand/10 hover:border-brand/30 transition-colors" title="View Report">
                            <BarChart3 className="w-3 h-3 text-muted-foreground" />
                          </button>
                          <button data-testid={`download-history-${i}`} onClick={() => downloadReport(sim.id, 'summary')}
                            className="w-7 h-7 border border-border rounded-md flex items-center justify-center hover:bg-muted transition-colors" title="Download PDF">
                            <FileText className="w-3 h-3 text-muted-foreground" />
                          </button>
                        </>
                      ) : (
                        <Badge className="bg-warning/10 text-warning border-none font-body text-[10px]">In Progress</Badge>
                      )}
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
